/**
 * Stable (马厩) - Cavalry training facility
 * Features: Dark gray curved roof with golden ornament, wooden fence, horse silhouette
 * Size: 3x3 tiles (visual ~2.8 x 2.4)
 * Roof Height: ~1.85 (lowest profile among training buildings)
 */

import * as THREE from 'three';

export function createStableMesh(
  group: THREE.Group,
  mainColor: THREE.Color,
  accentColor: THREE.Color
): void {
  const stoneMat = new THREE.MeshStandardMaterial({ color: 0x6a6a6a, roughness: 0.95 });
  const woodMat = new THREE.MeshStandardMaterial({ color: 0x5d4037, roughness: 0.85 });
  const darkWoodMat = new THREE.MeshStandardMaterial({ color: 0x3d2817, roughness: 0.8 });
  const lightWoodMat = new THREE.MeshStandardMaterial({ color: 0x8b6914, roughness: 0.75 });
  const roofMat = new THREE.MeshStandardMaterial({ color: 0x424242, roughness: 0.7 });
  const roofEdgeMat = new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.6 });
  const goldMat = new THREE.MeshStandardMaterial({ color: 0xd4af37, metalness: 0.7, roughness: 0.3 });
  const lanternMat = new THREE.MeshStandardMaterial({
    color: 0xffcc80,
    emissive: 0xff9800,
    emissiveIntensity: 0.4
  });
  const glowMat = new THREE.MeshStandardMaterial({
    color: 0xffcc80,
    emissive: 0xff9800,
    emissiveIntensity: 0.45
  });

  // === Stone Platform Base (2.8 x 2.4) ===
  const baseGeom = new THREE.BoxGeometry(2.8, 0.2, 2.4);
  const base = new THREE.Mesh(baseGeom, stoneMat);
  base.position.y = 0.1;
  base.receiveShadow = true;
  base.castShadow = true;
  group.add(base);

  // Stone edge
  const edgeGeom = new THREE.BoxGeometry(2.9, 0.05, 2.5);
  const edge = new THREE.Mesh(edgeGeom, new THREE.MeshStandardMaterial({ color: 0x5a5a5a, roughness: 0.9 }));
  edge.position.y = 0.22;
  group.add(edge);

  // === Main Building Structure ===
  // Back wall
  const backWallGeom = new THREE.BoxGeometry(1.9, 0.95, 0.12);
  const backWall = new THREE.Mesh(backWallGeom, darkWoodMat);
  backWall.position.set(0, 0.7, -0.9);
  backWall.castShadow = true;
  group.add(backWall);

  // Side walls (partial height)
  const sideWallGeom = new THREE.BoxGeometry(0.12, 0.95, 1.5);
  const leftWall = new THREE.Mesh(sideWallGeom, darkWoodMat);
  leftWall.position.set(-0.9, 0.7, -0.2);
  leftWall.castShadow = true;
  group.add(leftWall);

  const rightWall = new THREE.Mesh(sideWallGeom.clone(), darkWoodMat);
  rightWall.position.set(0.9, 0.7, -0.2);
  rightWall.castShadow = true;
  group.add(rightWall);

  // Wooden floor
  const floorGeom = new THREE.BoxGeometry(1.8, 0.06, 1.6);
  const floor = new THREE.Mesh(floorGeom, woodMat);
  floor.position.y = 0.26;
  floor.receiveShadow = true;
  group.add(floor);

  // === Support Pillars ===
  const pillarGeom = new THREE.BoxGeometry(0.08, 1.0, 0.08);
  const pillarPositions = [
    { x: -0.85, z: 0.5 },
    { x: 0.85, z: 0.5 },
    { x: -0.85, z: -0.8 },
    { x: 0.85, z: -0.8 },
  ];
  for (const pos of pillarPositions) {
    const pillar = new THREE.Mesh(pillarGeom, woodMat);
    pillar.position.set(pos.x, 0.72, pos.z);
    pillar.castShadow = true;
    group.add(pillar);
  }

  // === Dark Gray Curved Roof (Lowest) ===
  // Roof base
  const roofBaseGeom = new THREE.BoxGeometry(2.5, 0.1, 2.15);
  const roofBase = new THREE.Mesh(roofBaseGeom, roofMat);
  roofBase.position.y = 1.28;
  roofBase.castShadow = true;
  group.add(roofBase);

  // Curved roof cone
  const roofConeGeom = new THREE.ConeGeometry(1.4, 0.52, 4);
  const roofCone = new THREE.Mesh(roofConeGeom, roofMat);
  roofCone.position.y = 1.58;
  roofCone.rotation.y = Math.PI / 4;
  roofCone.castShadow = true;
  group.add(roofCone);

  // Roof edge
  const roofEdgeGeom = new THREE.BoxGeometry(2.65, 0.05, 2.3);
  const roofEdge = new THREE.Mesh(roofEdgeGeom, roofEdgeMat);
  roofEdge.position.y = 1.24;
  group.add(roofEdge);

  // Corner decorations
  const cornerGeom = new THREE.BoxGeometry(0.1, 0.05, 0.18);
  const cornerPositions = [
    { x: 1.25, z: 1.08, ry: -0.3 }, { x: -1.25, z: 1.08, ry: 0.3 },
    { x: 1.25, z: -1.08, ry: 0.3 }, { x: -1.25, z: -1.08, ry: -0.3 }
  ];
  for (const pos of cornerPositions) {
    const corner = new THREE.Mesh(cornerGeom, roofEdgeMat);
    corner.position.set(pos.x, 1.22, pos.z);
    corner.rotation.y = pos.ry;
    corner.rotation.z = 0.2;
    group.add(corner);
  }

  // === Golden Roof Ornament ===
  const ornamentGeom = new THREE.SphereGeometry(0.08, 8, 6);
  const ornament = new THREE.Mesh(ornamentGeom, goldMat);
  ornament.position.y = 1.88;
  group.add(ornament);

  // Ornament base
  const ornamentBaseGeom = new THREE.CylinderGeometry(0.04, 0.06, 0.08, 8);
  const ornamentBase = new THREE.Mesh(ornamentBaseGeom, goldMat);
  ornamentBase.position.y = 1.78;
  group.add(ornamentBase);

  // === Wooden Fence (Front) ===
  // Fence posts
  const fencePostGeom = new THREE.CylinderGeometry(0.035, 0.04, 0.6, 6);
  const fencePostPositions = [
    { x: -1.2, z: 0.65 }, { x: -0.6, z: 0.65 }, { x: 0, z: 0.65 },
    { x: 0.6, z: 0.65 }, { x: 1.2, z: 0.65 },
    { x: -1.2, z: 0.95 }, { x: 1.2, z: 0.95 },
  ];
  for (const pos of fencePostPositions) {
    const post = new THREE.Mesh(fencePostGeom, woodMat);
    post.position.set(pos.x, 0.5, pos.z);
    post.castShadow = true;
    group.add(post);
  }

  // Horizontal fence rails
  const frontRailGeom = new THREE.BoxGeometry(2.4, 0.05, 0.04);
  const frontRail1 = new THREE.Mesh(frontRailGeom, lightWoodMat);
  frontRail1.position.set(0, 0.4, 0.65);
  group.add(frontRail1);

  const frontRail2 = new THREE.Mesh(frontRailGeom.clone(), lightWoodMat);
  frontRail2.position.set(0, 0.58, 0.65);
  group.add(frontRail2);

  const frontRail3 = new THREE.Mesh(frontRailGeom.clone(), lightWoodMat);
  frontRail3.position.set(0, 0.76, 0.65);
  group.add(frontRail3);

  // Side fence rails
  const sideRailGeom = new THREE.BoxGeometry(0.04, 0.05, 0.35);
  for (const x of [-1.2, 1.2]) {
    for (const yOffset of [0.4, 0.58, 0.76]) {
      const sideRail = new THREE.Mesh(sideRailGeom, lightWoodMat);
      sideRail.position.set(x, yOffset, 0.8);
      group.add(sideRail);
    }
  }

  // === Gate Opening ===
  const gateFrameGeom = new THREE.BoxGeometry(0.06, 0.6, 0.08);
  const gateLeft = new THREE.Mesh(gateFrameGeom, darkWoodMat);
  gateLeft.position.set(-0.28, 0.5, 0.65);
  group.add(gateLeft);

  const gateRight = new THREE.Mesh(gateFrameGeom.clone(), darkWoodMat);
  gateRight.position.set(0.28, 0.5, 0.65);
  group.add(gateRight);

  // === Lantern ===
  const lanternBodyGeom = new THREE.BoxGeometry(0.08, 0.1, 0.08);
  const lanternBody = new THREE.Mesh(lanternBodyGeom, lanternMat);
  lanternBody.position.set(0, 1.0, 0.6);
  group.add(lanternBody);

  const lanternTopGeom = new THREE.ConeGeometry(0.05, 0.04, 4);
  const lanternTopMat = new THREE.MeshStandardMaterial({ color: 0x3d2817, roughness: 0.8 });
  const lanternTop = new THREE.Mesh(lanternTopGeom, lanternTopMat);
  lanternTop.position.set(0, 1.08, 0.6);
  lanternTop.rotation.y = Math.PI / 4;
  group.add(lanternTop);

  // === Interior Glow ===
  const glowGeom = new THREE.PlaneGeometry(0.45, 0.5);
  const glow = new THREE.Mesh(glowGeom, glowMat);
  glow.position.set(0, 0.5, 0.58);
  group.add(glow);

  // === Hay Bales ===
  const hayMat = new THREE.MeshStandardMaterial({ color: 0xc9a66b, roughness: 1.0 });
  const hayGeom = new THREE.BoxGeometry(0.3, 0.18, 0.22);

  const hay1 = new THREE.Mesh(hayGeom, hayMat);
  hay1.position.set(-0.6, 0.32, 0.32);
  group.add(hay1);

  const hay2 = new THREE.Mesh(hayGeom.clone(), hayMat);
  hay2.position.set(-0.6, 0.5, 0.32);
  hay2.rotation.y = 0.15;
  group.add(hay2);

  const hay3 = new THREE.Mesh(hayGeom.clone(), hayMat);
  hay3.position.set(0.6, 0.32, 0.32);
  group.add(hay3);

  // === Water Trough ===
  const troughGeom = new THREE.BoxGeometry(0.45, 0.18, 0.22);
  const troughMat = new THREE.MeshStandardMaterial({ color: 0x4a3a2a, roughness: 0.85 });
  const trough = new THREE.Mesh(troughGeom, troughMat);
  trough.position.set(0.55, 0.32, -0.55);
  group.add(trough);

  // Water
  const waterGeom = new THREE.BoxGeometry(0.38, 0.04, 0.15);
  const waterMat = new THREE.MeshStandardMaterial({ color: 0x4488aa, roughness: 0.3, transparent: true, opacity: 0.8 });
  const water = new THREE.Mesh(waterGeom, waterMat);
  water.position.set(0.55, 0.4, -0.55);
  group.add(water);

  // === Horse Silhouette Inside ===
  const horseMat = new THREE.MeshStandardMaterial({ color: 0x4a3a2a, roughness: 0.9 });

  // Horse body
  const horseBodyGeom = new THREE.SphereGeometry(0.18, 8, 6);
  horseBodyGeom.scale(1.5, 1.0, 0.85);
  const horseBody = new THREE.Mesh(horseBodyGeom, horseMat);
  horseBody.position.set(-0.35, 0.5, -0.4);
  group.add(horseBody);

  // Horse head
  const horseHeadGeom = new THREE.SphereGeometry(0.1, 8, 6);
  horseHeadGeom.scale(1.3, 1.0, 0.75);
  const horseHead = new THREE.Mesh(horseHeadGeom, horseMat);
  horseHead.position.set(-0.12, 0.65, -0.4);
  horseHead.rotation.z = 0.35;
  group.add(horseHead);

  // Horse legs
  const legGeom = new THREE.CylinderGeometry(0.025, 0.02, 0.26, 6);
  const legPositions = [
    { x: -0.48, z: -0.35 }, { x: -0.22, z: -0.35 },
    { x: -0.48, z: -0.45 }, { x: -0.22, z: -0.45 }
  ];
  for (const pos of legPositions) {
    const leg = new THREE.Mesh(legGeom, horseMat);
    leg.position.set(pos.x, 0.32, pos.z);
    group.add(leg);
  }

  // === Horseshoe on Wall ===
  const horseshoeGeom = new THREE.TorusGeometry(0.07, 0.012, 6, 12, Math.PI * 1.5);
  const horseshoeMat = new THREE.MeshStandardMaterial({ color: 0x555555, metalness: 0.7, roughness: 0.4 });
  const horseshoe = new THREE.Mesh(horseshoeGeom, horseshoeMat);
  horseshoe.position.set(0, 0.82, -0.83);
  horseshoe.rotation.x = Math.PI / 2;
  group.add(horseshoe);

  // === Saddle Rack ===
  const saddleRackGeom = new THREE.BoxGeometry(0.3, 0.05, 0.12);
  const saddleRack = new THREE.Mesh(saddleRackGeom, woodMat);
  saddleRack.position.set(0.65, 0.55, -0.75);
  group.add(saddleRack);

  // Saddle
  const saddleGeom = new THREE.BoxGeometry(0.22, 0.08, 0.18);
  const saddleMat = new THREE.MeshStandardMaterial({ color: 0x5d4037, roughness: 0.7 });
  const saddle = new THREE.Mesh(saddleGeom, saddleMat);
  saddle.position.set(0.65, 0.62, -0.75);
  group.add(saddle);
}
